import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {DataGridPro, GridColumns, GridRenderCellParams} from '@mui/x-data-grid-pro';
import LFCButton from '_components/inputs/LFCButton';
import {fmtThreeCommas, fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {renderCellExpand} from '_logics/LFCRenderUtil';
import {
  AccumFile,
  AccumObject,
  AccumQuery,
  AccumRecord,
  collectAllPages,
  csvCell,
  defaultPeriod,
  getAccumFiles,
  getAccumObjects,
  getAccumRecords,
  getOriginalUrl
} from '_logics/LFCPgHistoryAccumUtil';
import {downloadFromUrl} from '_logics/LFCSourceUtil';
import PaneHeader from './PaneHeader';
import PathTree from './PathTree';
import SplitPane, {MAIN_AREA_HEIGHT} from './SplitPane';

const date = (value: string) => fmtYYYYMMDDHHmmss(value) ?? '—';
const DAY_MS = 24 * 60 * 60 * 1000;
/** キー名の入力が止まってから検索するまでの待ち時間 */
const KEY_DEBOUNCE_MS = 400;
type Preset = '24h' | '7d' | '30d' | 'custom';
const PRESET_DAYS: Record<Exclude<Preset, 'custom'>, number> = {'24h': 1, '7d': 7, '30d': 30};
const PRESETS: {value: Preset; label: string}[] = [
  {value: '24h', label: 'accum.preset24h'},
  {value: '7d', label: 'accum.preset7d'},
  {value: '30d', label: 'accum.preset30d'},
  {value: 'custom', label: 'accum.presetCustom'}
];
type GridTab = 'records' | 'objects';
const gridProps = {
  density: 'compact',
  showCellRightBorder: true,
  showColumnRightBorder: true,
  disableSelectionOnClick: true,
  disableColumnMenu: true,
  hideFooter: true
} as const;

/** グリッドの行がないときの表示（検索前は何も出さない） */
function EmptyOverlay({message}: {message?: string}) {
  return (
    <Stack sx={{height: '100%'}} alignItems="center" justifyContent="center">
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  );
}

export default function AccumulationLogs({selectedDevice: device}: {selectedDevice: string}) {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const ctx = useMemo(() => ({t, snack: enqueueSnackbar}), [t, enqueueSnackbar]);
  const [files, setFiles] = useState<AccumFile[]>([]);
  // 取得済みファイル一覧がどの端末のものか（切替直後に前端末の結果で「データなし」を出さない）
  const [filesDevice, setFilesDevice] = useState('');
  const [file, setFile] = useState('');
  const [preset, setPreset] = useState<Preset>('7d');
  const [[start, end], setCustomPeriod] = useState(defaultPeriod);
  const [keyInput, setKeyInput] = useState('');
  const [keyFilter, setKeyFilter] = useState('');
  const [tab, setTab] = useState<GridTab>('records');
  const [records, setRecords] = useState<AccumRecord[]>([]);
  const [objects, setObjects] = useState<AccumObject[]>([]);
  const [activeQuery, setActiveQuery] = useState<AccumQuery | null>(null);
  const [moreRecords, setMoreRecords] = useState(false);
  const [moreObjects, setMoreObjects] = useState(false);
  const [detail, setDetail] = useState<AccumRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [filesBusy, setFilesBusy] = useState(false);
  const [error, setError] = useState('');
  // ファイル一覧と検索結果は別々に古い応答を捨てる（検索の再実行で一覧の取得を無効にしない）
  const filesEpoch = useRef(0);
  const epoch = useRef(0);

  const invalidate = () => {
    epoch.current++;
    setRecords([]);
    setObjects([]);
    setActiveQuery(null);
    setMoreRecords(false);
    setMoreObjects(false);
    setDetail(null);
    setBusy(false);
    setError('');
  };
  useEffect(() => {
    const current = ++filesEpoch.current;
    setFiles([]);
    setFilesDevice('');
    setFile('');
    setFilesBusy(!!device);
    if (device)
      getAccumFiles(ctx, device)
        .then(rows => {
          if (current !== filesEpoch.current) return;
          setFiles(rows);
          setFilesDevice(device);
        })
        .catch(() => {
          if (current === filesEpoch.current) setError('accum.loadFailed');
        })
        .finally(() => {
          if (current === filesEpoch.current) setFilesBusy(false);
        });
    return () => {
      filesEpoch.current++;
    };
  }, [ctx, device]);

  useEffect(() => {
    const timer = setTimeout(() => setKeyFilter(keyInput), KEY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyInput]);

  // ファイル選択・期間・キー名が変わるたびに自動で検索する
  useEffect(() => {
    invalidate();
    // 端末切替直後は前の端末のファイル名が残っているので問い合わせない
    if (!device || !file || filesDevice !== device) return;
    let startAt: Date;
    let endAt: Date;
    if (preset === 'custom') {
      startAt = new Date(start);
      endAt = new Date(end);
    } else {
      endAt = new Date();
      startAt = new Date(endAt.getTime() - PRESET_DAYS[preset] * DAY_MS);
    }
    if (
      !Number.isFinite(startAt.getTime()) ||
      !Number.isFinite(endAt.getTime()) ||
      startAt >= endAt
    ) {
      setError('accum.invalidPeriod');
      return;
    }
    const current = epoch.current;
    const parameters = {
      device,
      file_path: file,
      start_at: startAt.toISOString(),
      end_at: endAt.toISOString(),
      key_filter: keyFilter
    };
    setBusy(true);
    Promise.all([getAccumRecords(ctx, parameters), getAccumObjects(ctx, parameters)])
      .then(([recordPage, originalPage]) => {
        if (current !== epoch.current) return;
        setActiveQuery(parameters);
        setRecords(recordPage.rows);
        setObjects(originalPage.rows);
        setMoreRecords(recordPage.hasMore);
        setMoreObjects(originalPage.hasMore);
      })
      .catch(() => {
        if (current === epoch.current) setError('accum.loadFailed');
      })
      .finally(() => {
        if (current === epoch.current) setBusy(false);
      });
    return () => {
      epoch.current++;
    };
  }, [ctx, device, file, filesDevice, preset, start, end, keyFilter]);

  const choosePreset = (next: Preset) => {
    // 期間指定に切り替えたら、直前のプリセットの期間を入力の初期値にする
    if (next === 'custom' && preset !== 'custom')
      setCustomPeriod(defaultPeriod(new Date(), PRESET_DAYS[preset]));
    setPreset(next);
  };
  const loadMore = async (kind: GridTab) => {
    if (!activeQuery) return;
    const current = epoch.current;
    setBusy(true);
    setError('');
    try {
      if (kind === 'records') {
        const after = records[records.length - 1]?.id ?? 0;
        const {rows, hasMore} = await getAccumRecords(ctx, activeQuery, after);
        if (current !== epoch.current) return;
        if (rows.some(row => row.id <= after)) throw new Error('Invalid cursor');
        setRecords(prev => [...prev, ...rows]);
        setMoreRecords(hasMore);
      } else {
        const after = objects[objects.length - 1]?.id ?? 0;
        const {rows, hasMore} = await getAccumObjects(ctx, activeQuery, after);
        if (current !== epoch.current) return;
        if (rows.some(row => row.id <= after)) throw new Error('Invalid cursor');
        setObjects(prev => [...prev, ...rows]);
        setMoreObjects(hasMore);
      }
    } catch {
      if (current === epoch.current) setError('accum.loadFailed');
    } finally {
      if (current === epoch.current) setBusy(false);
    }
  };
  const exportCsv = async () => {
    if (!activeQuery) return;
    const current = epoch.current;
    setBusy(true);
    setError('');
    try {
      const rows = await collectAllPages(
        after => getAccumRecords(ctx, activeQuery, after),
        () => current !== epoch.current
      );
      if (current !== epoch.current) return;
      const columns: (keyof AccumRecord)[] = [
        'device',
        'file_path',
        'record_key',
        'occurred_at',
        'collected_at',
        'body',
        'object_id'
      ];
      const csv =
        '﻿' +
        [
          columns.map(csvCell).join(','),
          ...rows.map(row => columns.map(col => csvCell(row[col])).join(','))
        ].join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8'}));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'accumulation.csv';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (current === epoch.current) setError('accum.exportFailed');
    } finally {
      if (current === epoch.current) setBusy(false);
    }
  };
  const download = async (objectId: string) => {
    const current = epoch.current;
    setBusy(true);
    setError('');
    try {
      const response = await getOriginalUrl(ctx, objectId);
      if (current === epoch.current) await downloadFromUrl(response.url, response.file_name);
    } catch {
      if (current === epoch.current) setError('accum.downloadFailed');
    } finally {
      if (current === epoch.current) setBusy(false);
    }
  };
  const dateColumn = (field: 'occurred_at' | 'collected_at', headerName: string) => ({
    field,
    headerName,
    width: 190,
    valueFormatter: ({value}: {value: string}) => date(value)
  });
  const numberColumn = (field: 'size_bytes' | 'record_count', headerName: string) => ({
    field,
    headerName,
    width: 130,
    align: 'right' as const,
    headerAlign: 'right' as const,
    valueFormatter: ({value}: {value: number}) => fmtThreeCommas(value)
  });
  const recordColumns: GridColumns<AccumRecord> = [
    dateColumn('occurred_at', t('accum.occurredAt')),
    dateColumn('collected_at', t('accum.collectedAt')),
    {field: 'record_key', headerName: t('accum.key'), width: 200, renderCell: renderCellExpand},
    {
      field: 'body',
      headerName: t('accum.body'),
      flex: 1,
      minWidth: 300,
      renderCell: renderCellExpand
    }
  ];
  const objectColumns: GridColumns<AccumObject> = [
    dateColumn('collected_at', t('accum.collectedAt')),
    numberColumn('size_bytes', t('accum.size')),
    {field: 'parse_status', headerName: t('accum.status'), width: 120},
    numberColumn('record_count', t('accum.count')),
    {
      field: 'download',
      headerName: '',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (prms: GridRenderCellParams<unknown, AccumObject>) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={busy}
          onClick={() => download(prms.row.object_id)}
        >
          {t('accum.download')}
        </Button>
      )
    }
  ];
  const treeItems = useMemo(
    () =>
      files.map(row => ({
        path: row.file_path,
        meta: t('accum.fileMeta', {count: row.record_count, date: date(row.last_collected_at)})
      })),
    [files, t]
  );
  if (!device) return <Alert severity="info">{t('端末を選択してください')}</Alert>;
  if (!files.length)
    return (
      <Stack spacing={2} sx={{pb: 2}}>
        {error && <Alert severity="error">{t(error)}</Alert>}
        {filesBusy && <CircularProgress size={24} aria-label={t('accum.loading')} />}
        {filesDevice === device && !error && <Alert severity="info">{t('accum.noFiles')}</Alert>}
      </Stack>
    );
  const selectedFile = files.find(row => row.file_path === file);
  const tabLabel = (label: string, count: number, more: boolean) =>
    `${t(label)} (${fmtThreeCommas(count)}${more ? '+' : ''})`;
  const shownCount = tab === 'records' ? records.length : objects.length;
  const hasMore = tab === 'records' ? moreRecords : moreObjects;
  const emptyMessage = activeQuery
    ? t(tab === 'records' ? 'accum.noRecords' : 'accum.noOriginals')
    : '';
  return (
    <Box sx={{height: MAIN_AREA_HEIGHT}}>
      <SplitPane
        left={
          <PathTree
            label={t('accum.file')}
            items={treeItems}
            selectedPath={file || null}
            onSelect={setFile}
          />
        }
      >
        {!file ? (
          <Typography color="text.secondary">{t('accum.selectFile')}</Typography>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minHeight: 0,
              pr: 1.5,
              pb: 1
            }}
          >
            <PaneHeader
              title={file}
              meta={
                selectedFile &&
                t('accum.fileHeaderMeta', {
                  n: fmtThreeCommas(selectedFile.record_count),
                  date: date(selectedFile.last_collected_at)
                })
              }
              note={t('accum.periodNote')}
            />
            <Box
              sx={{display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', flexShrink: 0}}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                color="primary"
                value={preset}
                aria-label={t('accum.period')}
                onChange={(_, next: Preset | null) => next && choosePreset(next)}
              >
                {PRESETS.map(option => (
                  <ToggleButton key={option.value} value={option.value} sx={{px: 1.5}}>
                    {t(option.label)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {preset === 'custom' && (
                <>
                  <TextField
                    size="small"
                    label={t('accum.start')}
                    type="datetime-local"
                    InputLabelProps={{shrink: true}}
                    value={start}
                    onChange={e => setCustomPeriod([e.target.value, end])}
                    sx={{width: 220}}
                  />
                  <TextField
                    size="small"
                    label={t('accum.end')}
                    type="datetime-local"
                    InputLabelProps={{shrink: true}}
                    value={end}
                    onChange={e => setCustomPeriod([start, e.target.value])}
                    sx={{width: 220}}
                  />
                </>
              )}
              <TextField
                size="small"
                label={t('accum.keyFilter')}
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                sx={{width: 200}}
              />
              <Box sx={{ml: 'auto'}}>
                <LFCButton
                  size="small"
                  color="primary"
                  disabled={busy || !activeQuery}
                  onClick={exportCsv}
                >
                  {t('accum.exportCsv')}
                </LFCButton>
              </Box>
            </Box>
            {error && (
              <Alert severity="error" sx={{flexShrink: 0}}>
                {t(error)}
              </Alert>
            )}
            <Tabs
              value={tab}
              onChange={(_, next: GridTab) => setTab(next)}
              sx={{
                minHeight: 36,
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {minHeight: 36, py: 0.5, textTransform: 'none'}
              }}
            >
              <Tab value="records" label={tabLabel('accum.records', records.length, moreRecords)} />
              <Tab
                value="objects"
                label={tabLabel('accum.originals', objects.length, moreObjects)}
              />
            </Tabs>
            <Box sx={{flex: 1, minHeight: 0, position: 'relative'}}>
              <Box sx={{position: 'absolute', inset: 0}}>
                {tab === 'records' ? (
                  <DataGridPro
                    aria-label={t('accum.records')}
                    rows={records}
                    columns={recordColumns}
                    getRowId={row => row.id}
                    loading={busy}
                    onRowClick={params => setDetail(params.row as AccumRecord)}
                    selectionModel={detail ? [detail.id] : []}
                    components={{NoRowsOverlay: EmptyOverlay}}
                    componentsProps={{noRowsOverlay: {message: emptyMessage}}}
                    sx={{'& .MuiDataGrid-row': {cursor: 'pointer'}}}
                    {...gridProps}
                  />
                ) : (
                  <DataGridPro
                    aria-label={t('accum.originals')}
                    rows={objects}
                    columns={objectColumns}
                    getRowId={row => row.id}
                    loading={busy}
                    initialState={{pinnedColumns: {right: ['download']}}}
                    components={{NoRowsOverlay: EmptyOverlay}}
                    componentsProps={{noRowsOverlay: {message: emptyMessage}}}
                    {...gridProps}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
              <Typography variant="caption" color="text.secondary">
                {t('accum.shown', {n: fmtThreeCommas(shownCount)})}
              </Typography>
              {busy && <CircularProgress size={16} aria-label={t('accum.loading')} />}
              {hasMore && (
                <Box sx={{ml: 'auto'}}>
                  <LFCButton
                    size="small"
                    color="secondary"
                    disabled={busy}
                    onClick={() => loadMore(tab)}
                  >
                    {t('accum.loadMore')}
                  </LFCButton>
                </Box>
              )}
            </Box>
            {tab === 'records' && detail && (
              <RecordDetail
                record={detail}
                busy={busy}
                onDownload={() => download(detail.object_id)}
                onClose={() => setDetail(null)}
              />
            )}
          </Box>
        )}
      </SplitPane>
    </Box>
  );
}

/** グリッドの下に出すレコード詳細（本文を省略せずに表示する） */
function RecordDetail({
  record,
  busy,
  onDownload,
  onClose
}: {
  record: AccumRecord;
  busy: boolean;
  onDownload: () => void;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const field = (label: string, value: string) => (
    <Box sx={{display: 'flex', gap: 0.5, alignItems: 'baseline', minWidth: 0}}>
      <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
        {t(label)}
      </Typography>
      <Typography variant="body2" sx={{overflowWrap: 'anywhere'}}>
        {value}
      </Typography>
    </Box>
  );
  return (
    <Box
      component="section"
      aria-label={t('accum.detail')}
      sx={{
        height: '30%',
        minHeight: 140,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1
      }}
    >
      <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2}}>
        {field('accum.occurredAt', date(record.occurred_at))}
        {field('accum.collectedAt', date(record.collected_at))}
        {field('accum.key', record.record_key ?? '—')}
        <Box sx={{ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5}}>
          {record.object_id && (
            <LFCButton size="small" color="primary" disabled={busy} onClick={onDownload}>
              {t('accum.download')}
            </LFCButton>
          )}
          <IconButton size="small" aria-label={t('閉じる')} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box
        component="pre"
        sx={{
          flex: 1,
          minHeight: 0,
          m: 0,
          p: 1,
          overflow: 'auto',
          fontFamily: 'monospace',
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          bgcolor: 'action.hover',
          borderRadius: 1
        }}
      >
        {record.body}
      </Box>
    </Box>
  );
}
